import { ORG_ENV, ECOSYSTEM, VNCONFIGS_GLOBAL_REQUIRED } from '../constants'

const ENV_PUBLIC_RUNTIME = `${ORG_ENV}_public_runtime`

/**
 * retrieve the configuration from the global VNAPI
 * @param {*} state
 */
export const getConfiguration = (state) => {
  return state.VNConfigurations?.get('configuration')
}

export const getGlobalConfiguration = (state) => {
  if (!VNCONFIGS_GLOBAL_REQUIRED) {
    return null
  }

  return state.VNConfigurations?.get('globalConfiguration')
}

/**
 * Get the configuration key for the keys in the VNConfiguration response
 * @param {String} configName - (Optional) Could be an instance name or an ecosystem name
 * @returns {String}
 */
export const getConfigKey = (configName) => {
  if (!configName) {
    return ENV_PUBLIC_RUNTIME
  }

  return `${ENV_PUBLIC_RUNTIME}_${configName}`
}

export const getVenueIdConfigKey = () => {
  return getConfigKey(process.env.REACT_APP_VENUE_UUID)
}

export const getInstanceConfigKey = () => {
  return getConfigKey(process.env.REACT_APP_INSTANCE_NAME)
}

export const getEcosystemConfigKey = () => {
  return getConfigKey(ECOSYSTEM)
}

/**
 * Checks to see if a key exists by instance level, then venue level (by venue UUID) in VNConfiguration object
 * If it doesn't, it'll return null, otherwise it'll return the value
 * @link https://shift4.atlassian.net/wiki/spaces/venuenext/pages/1978892509
 * @param {Obj} config - The full configuration in redux, this includes ALL VNConfigurations
 * @param {String} key - What key you are looking for
 */
export const checkForInstanceLevelConfigurationKey = (config, key) => {
  const instanceConfigKey = getInstanceConfigKey()
  if (config?.has(instanceConfigKey) && config.get(instanceConfigKey)[key]) {
    return config.get(instanceConfigKey)[key]
  }

  const venueConfigKey = getVenueIdConfigKey()
  if (config?.has(venueConfigKey) && config.get(venueConfigKey)[key]) {
    return config.get(venueConfigKey)[key]
  }

  return null
}

/**
 * Checks to see if a key exists in an ecosystem level VNConfiguration object
 * If it doesn't, it'll return null, otherwise it'll return the value
 * @param {Object} globalConfig - The full global configuration in redux, this includes ALL VNConfigurations
 * @param {String} key - What key you are looking for
 */
export const checkForEcosystemLevelConfigurationKey = (globalConfig, key) => {
  const ecosystemConfigKey = getEcosystemConfigKey()
  if (globalConfig?.has(ecosystemConfigKey) && globalConfig.get(ecosystemConfigKey)[key]) {
    return globalConfig.get(ecosystemConfigKey)[key]
  }

  return null
}

/**
 * Get value by the given key. This will look for the instance level first, and if one is not found, it'll default to the org level value.
 * @param {*} state - Global State
 * @param {String} key - What key you are looking for
 */
export const getByKey = (state, key) => {
  const config = getConfiguration(state)
  const orgValue = config?.get(ENV_PUBLIC_RUNTIME)?.[key]
  const instanceValue = checkForInstanceLevelConfigurationKey(config, key)

  if (VNCONFIGS_GLOBAL_REQUIRED) {
    const globalConfig = getGlobalConfiguration(state)
    const globalValue = globalConfig?.get(ENV_PUBLIC_RUNTIME)?.[key]
    const ecosystemValue = checkForEcosystemLevelConfigurationKey(globalConfig, key)

    return instanceValue || orgValue || ecosystemValue || globalValue
  }

  return instanceValue || orgValue
}

/**
 * This function gets a boolean by the key. This will look for the instance level first, and if one is not found, it'll default to the org level value.
 * @param {*} state - Global State
 * @param {String} key - What key you are looking for
 * @returns {boolean} true if the string is true or yes
 */
export const getByKey_Boolean = (state, key) => {
  const val = getByKey(state, key)
  if (val && (val === 'true' || val === 'yes')) {
    return true
  }
  return false
}

/**
 * Get an array by the given key. This will look for the instance level first, and if one is not found, it'll default to the org level value.
 * @param {*} state - Global State
 * @param {String} key - What key you are looking for
 * @param {Boolean} blendDown - If true, values from global, ecosystem, org, and instance levels will be merged and returned.
 */
export const getByKey_Array = (state, key, blendDown = false) => {
  const config = getConfiguration(state)
  const orgValue = config?.get(ENV_PUBLIC_RUNTIME)?.[key]
  const instanceValue = checkForInstanceLevelConfigurationKey(config, key)

  if (blendDown) {
    const globalConfig = getGlobalConfiguration(state)

    const globalValue = globalConfig?.get(ENV_PUBLIC_RUNTIME)?.[key]
    const ecosystemValue = checkForEcosystemLevelConfigurationKey(globalConfig, key)

    const allValues = [globalValue, ecosystemValue, orgValue, instanceValue]
    const mergedArray = []

    for (const value of allValues) {
      if (typeof value === 'string' && value.length) {
        mergedArray.push(...value.split('|'))
      }
    }

    return mergedArray
  }

  if (instanceValue) {
    return instanceValue.split('|')
  }

  if (orgValue) {
    return orgValue.split('|')
  }

  return []
}

/**
 * Get a boolean if the given key exists or not. This will look for the instance level first, and if one is not found, it'll default to the org level value.
 * @param {*} state - Global State
 * @param {String} key - What key you are looking for
 */
export const getByKey_Exists = (state, key) => {
  const config = getConfiguration(state)
  const orgValue = config?.get(ENV_PUBLIC_RUNTIME)?.[key]
  const instanceValue = checkForInstanceLevelConfigurationKey(config, key)

  if (VNCONFIGS_GLOBAL_REQUIRED) {
    const globalConfig = getGlobalConfiguration(state)
    const globalValue = globalConfig?.get(ENV_PUBLIC_RUNTIME)?.[key]
    const ecosystemValue = checkForEcosystemLevelConfigurationKey(globalConfig, key)

    return !!(instanceValue || orgValue || ecosystemValue || globalValue)
  }

  return !!(instanceValue || orgValue)
}
